import React, { ReactElement, useEffect, useMemo, useRef } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'

import { AutocompleteItemType, FormInputAutocomplete, FormInputAutocompleteProps } from './FormInputAutocomplete'

import { useFocusOnFirstError } from 'Utils/helpers/useFocusOnFirstError'

type Props<ItemType extends AutocompleteItemType> = Pick<
  UseFormMethods,
  'register' | 'errors' | 'control' | 'setValue'
> &
  FormInputAutocompleteProps<ItemType> & {
    name: string
    onChangeValueMapper?: (value: ItemType | null | undefined, isNew: boolean) => unknown
    sortItemsFunction?: (a: ItemType, b: ItemType) => number
  }

export const FormInputAutocompleteController = <ItemType extends AutocompleteItemType>({
  name,
  items,
  register,
  control,
  itemIdKey = 'id',
  itemRenderKey = 'name',
  onChangeValueMapper,
  sortItemsFunction,
  errors,
  setValue,

  ...props
}: Props<ItemType>): ReactElement | null => {
  const fieldRef = useRef<HTMLInputElement>(null)
  const errorText = useMemo(() => (errors[name] ? errors[name].message : ''), [errors[name]])

  useEffect(() => {
    register({ name })
  }, [])

  const value = useWatch({
    control,
    name,
    defaultValue: undefined
  }) as ItemType | undefined

  const itemsWithSelection = useMemo(() => {
    if (value == null || typeof value !== 'object') {
      return items
    }

    const selectedId = value[itemIdKey]
    if (selectedId == null || selectedId === '') {
      return items
    }

    if (items.some((item) => item[itemIdKey] === selectedId)) {
      return items
    }

    if (typeof value[itemRenderKey] !== 'string' || value[itemRenderKey] === '') {
      return items
    }

    return [value, ...items]
  }, [items, value, itemIdKey, itemRenderKey])

  const selectedItemFitler: (item: ItemType) => boolean = onChangeValueMapper
    ? (item) => onChangeValueMapper(item, false) === value
    : (item) => item[itemIdKey] === value?.[itemIdKey]

  const selectedItem = itemsWithSelection.find(selectedItemFitler)

  useFocusOnFirstError({ fieldRef, errors, name })

  if (Object.keys(itemsWithSelection).length === 0 && !props.allowNewItem) {
    return null
  }

  return (
    <>
      <div className="col-12">
        <FormInputAutocomplete
          inputRef={fieldRef}
          items={itemsWithSelection}
          itemIdKey={itemIdKey}
          itemRenderKey={itemRenderKey}
          onChange={(value, isNew) =>
            setValue(name, onChangeValueMapper ? onChangeValueMapper(value, isNew) : value, { shouldValidate: true })
          }
          sortItemsFunction={sortItemsFunction}
          errorText={errorText}
          {...props}
          selectedItemId={selectedItem?.[itemIdKey] ?? value?.[itemIdKey]}
        />
      </div>
    </>
  )
}
